import { parsePhoneNumberFromString } from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js';

export type MobileValidationError = 'mobile_number_required' | 'invalid_mobile_number';

function resolveCountryCode(country?: string | null): CountryCode | undefined {
  if (!country) {
    return undefined;
  }

  const normalizedCountry = country.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(normalizedCountry)) {
    return undefined;
  }

  return normalizedCountry as CountryCode;
}

export function normalizeMobileNumber(input: string, country?: string | null): string | null {
  const trimmed = input.trim();
  if (!trimmed) {
    return null;
  }

  const parsed = parsePhoneNumberFromString(trimmed, resolveCountryCode(country));
  if (!parsed || !parsed.isValid()) {
    return null;
  }

  return parsed.number;
}

export function isValidMobileNumber(input: string, country?: string | null): boolean {
  return normalizeMobileNumber(input, country) !== null;
}

export function validateMobileNumber(
  input: string,
  country?: string | null
): { isValid: boolean; normalized: string | null; error: MobileValidationError | null } {
  if (!input.trim()) {
    return {
      isValid: false,
      normalized: null,
      error: 'mobile_number_required',
    };
  }

  const normalized = normalizeMobileNumber(input, country);
  if (!normalized) {
    return {
      isValid: false,
      normalized: null,
      error: 'invalid_mobile_number',
    };
  }

  return {
    isValid: true,
    normalized,
    error: null,
  };
}

// Backward-compatible wrappers for existing Saudi-specific callers
export function normalizeSaudiMobile(input: string): string | null {
  return normalizeMobileNumber(input, 'SA');
}

export function isValidSaudiMobile(input: string): boolean {
  return isValidMobileNumber(input, 'SA');
}
